import {
  FC,
  MouseEvent,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Range } from "react-date-range";
import { Button, Stack, Typography } from "@mui/material";
import { Event, ExpandMore } from "@mui/icons-material";

import CustomDateRangePicker from "../date-range-picker";
import { formatDateOrTime } from "@/utils";
import { PRESET_RANGES } from "@/constants";
import { tokens } from "@/tokens";


interface IDateRangeFilter {
  value?: Pick<Range, "startDate" | "endDate">;
  onSelectDate?: (dates: Pick<Range, "startDate" | "endDate">) => void;
  fullWidth?: boolean;
}

const DateRangeFilter: FC<IDateRangeFilter> = ({
  value,
  onSelectDate,
  fullWidth = false,
}): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openDatePickerModal = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const dateLabel = useMemo(() => {
    const hasStart = Boolean(value?.startDate);
    const hasEnd = Boolean(value?.endDate);

    if (!hasStart && !hasEnd) {
      return "All Time";
    }

    if (hasStart && hasEnd) {
      const selectedStartDate = formatDateOrTime(value!.startDate!, "date");
      const selectedEndDate = formatDateOrTime(value!.endDate!, "date");

      const presetSingle = PRESET_RANGES.find((preset) => {
        const presetStartDate = formatDateOrTime(
          preset.range.startDate,
          "date"
        );
        const presetEndDate = formatDateOrTime(preset.range.endDate, "date");

        return (
          selectedStartDate === presetStartDate &&
          selectedEndDate === presetEndDate
        );
      });

      return presetSingle?.label ?? `${selectedStartDate} - ${selectedEndDate}`;
    }

    if (hasStart && !hasEnd) {
      return `${formatDateOrTime(value!.startDate!, "date")} - Present`;
    }

    if (!hasStart && hasEnd) {
      return `Until ${formatDateOrTime(value!.endDate!, "date")}`;
    }

    return "All Time";
  }, [value]);

  return (
    <>
      <Button
        variant="outlined"
        onClick={openDatePickerModal}
        id="date-range-picker-anchor"
        aria-controls={anchorEl ? "date-range-picker-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? "true" : undefined}
        sx={{
          borderColor: tokens.border.default,
          borderRadius: "8px",
          bgcolor: "#fff",
          minWidth: fullWidth ? "100%" : "auto",
          py: 0.75,
          px: 2,
          flex: {
            xs: 1,
            sm: "initial",
          },
          "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
          ...(fullWidth && {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Event sx={{ fontSize: 16, color: tokens.text.secondary }} />
          <Typography sx={{ fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.semibold, color: tokens.text.body, whiteSpace: "nowrap" }}>
            {dateLabel}
          </Typography>
          <ExpandMore sx={{ color: tokens.text.muted, fontSize: 18 }} />
        </Stack>
      </Button>
      <CustomDateRangePicker
        anchorEl={anchorEl}
        rangeValue={value}
        onClose={() => setAnchorEl(null)}
        onDateSelect={onSelectDate}
      />
    </>
  );
};

export default DateRangeFilter;
